/**
 * SignalRConnectionService
 *
 * Concrete implementation of ISignalRConnection
 * Handles WebSocket/SignalR protocol lifecycle only
 *
 * Single Responsibility: Connection lifecycle management
 */

import * as signalR from '@microsoft/signalr';
import type { ISignalRConnection } from '../interfaces';
import { ConnectionState } from '../types';

export class SignalRConnectionService implements ISignalRConnection {
  private connection: signalR.HubConnection | null = null;
  private currentState: ConnectionState = ConnectionState.Disconnected;

  private onConnectedCallbacks: Set<() => void> = new Set();
  private onDisconnectedCallbacks: Set<(error?: Error) => void> = new Set();
  private onReconnectingCallbacks: Set<(error?: Error) => void> = new Set();
  private onReconnectedCallbacks: Set<() => void> = new Set();

  get state(): ConnectionState {
    if (!this.connection) return this.currentState;

    // Sync with actual connection state
    switch (this.connection.state) {
      case signalR.HubConnectionState.Connecting:
        return (this.currentState = ConnectionState.Connecting);
      case signalR.HubConnectionState.Connected:
        return (this.currentState = ConnectionState.Connected);
      case signalR.HubConnectionState.Reconnecting:
        return (this.currentState = ConnectionState.Reconnecting);
      case signalR.HubConnectionState.Disconnected:
        return (this.currentState = ConnectionState.Disconnected);
      default:
        return ConnectionState.Failed;
    }
  }

  isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }

  async connect(url: string): Promise<void> {
    if (this.connection) {
      throw new Error('Already connected or connecting');
    }

    try {
      this.currentState = ConnectionState.Connecting;

      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(url, {
          transport:
            signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
          skipNegotiation: false,
          timeout: 30000,
          withCredentials: false,
        })
        .withAutomaticReconnect([1000, 2000, 5000, 10000, 30000, 60000])
        .withHubProtocol(new signalR.JsonHubProtocol())
        .configureLogging(signalR.LogLevel.Information)
        .build();

      // Wire up lifecycle callbacks
      this.setupLifecycleCallbacks();

      await this.connection.start();
      this.currentState = ConnectionState.Connected;
      this.invokeCallbacks(this.onConnectedCallbacks);
    } catch (error) {
      this.currentState = ConnectionState.Failed;
      this.connection = null;
      throw error;
    }
  }

  async disconnect(): Promise<void> {
    if (!this.connection) {
      throw new Error('Not connected');
    }

    try {
      this.currentState = ConnectionState.Disconnecting;
      await this.connection.stop();
      this.currentState = ConnectionState.Disconnected;
      this.connection = null;
    } catch (error) {
      this.currentState = ConnectionState.Failed;
      throw error;
    }
  }

  async invoke<T = void>(method: string, ...args: unknown[]): Promise<T> {
    if (!this.connection) {
      throw new Error('Not connected');
    }

    if (this.connection.state !== signalR.HubConnectionState.Connected) {
      throw new Error(`Connection not in Connected state: ${this.connection.state}`);
    }

    return this.connection.invoke<T>(method, ...args);
  }

  on(event: string, handler: (...args: unknown[]) => void | Promise<void>): () => void {
    if (!this.connection) {
      throw new Error('Not connected');
    }

    this.connection.on(event, handler);

    // Return unregister function
    return () => {
      this.connection?.off(event, handler);
    };
  }

  off(event: string): void {
    if (this.connection) {
      this.connection.off(event);
    }
  }

  onConnected(callback: () => void): void {
    this.onConnectedCallbacks.add(callback);
  }

  onDisconnected(callback: (error?: Error) => void): void {
    this.onDisconnectedCallbacks.add(callback);
  }

  onReconnecting(callback: (error?: Error) => void): void {
    this.onReconnectingCallbacks.add(callback);
  }

  onReconnected(callback: () => void): void {
    this.onReconnectedCallbacks.add(callback);
  }

  // Private methods

  private setupLifecycleCallbacks(): void {
    if (!this.connection) return;

    this.connection.onreconnecting((error?: Error) => {
      this.currentState = ConnectionState.Reconnecting;
      this.invokeCallbacks(this.onReconnectingCallbacks, error);
    });

    this.connection.onreconnected(() => {
      this.currentState = ConnectionState.Connected;
      this.invokeCallbacks(this.onReconnectedCallbacks);
    });

    this.connection.onclose((error?: Error) => {
      this.currentState = ConnectionState.Disconnected;
      this.connection = null;
      this.invokeCallbacks(this.onDisconnectedCallbacks, error);
    });
  }

  private invokeCallbacks<T extends Function>(
    callbacks: Set<T>,
    ...args: unknown[]
  ): void {
    callbacks.forEach((cb) => {
      try {
        (cb as Function)(...args);
      } catch (error) {
        console.error('[SignalRConnection] Callback error:', error);
      }
    });
  }
}
